import { TextField } from '@mui/material';

import MyDialog from "~/mui/MyDialog";
import { useTrans } from "~/hooks/useTrans";
import { checkEmail } from "~/utils/utils";
import { myUseState } from "~/hooks/myUseState";
import ButtonLink from "~/components/ButtonLink";
import { userStore } from "~/stores/user";
import { WALLET_DOCUMENTATION, WALLET_REGISTER } from "~/const";
import { IClientProject } from "~/providers/client-interfaces";
import { IAdminProject } from "~/providers/admin-interfaces";
import { apiCreateProjectWallet, apiCreateProjectWalletInvitation } from "~/providers/api";
import ExternalLink from "~/components/ExternalLink";
import { useLang } from "~/hooks/useLang";

import "./style.less";

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
}

interface IFormState {
	email: string;
}

interface IWalletConnect {
	projectData: IClientProject | IAdminProject;
	switchWallet?: boolean;
	onClose?: () => void;
	onSave?: (lastUpdate: string) => void;
	onChange?: (isChanged: boolean) => void;
}

export default function WalletConnect({
	projectData,
	switchWallet = false,
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IWalletConnect) {
	const { trans } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
	});
	const { state: stateForm, updateState: updateStateForm } = myUseState<IFormState>({
		email: "",
	});
	const userEmail = userStore.getState().user.email;

	function getWasChanged(stateChanges: Partial<IFormState>): boolean {
		const newStateObj = {
			...stateForm,
			...stateChanges,
		};

		return newStateObj.email !== "";
	}

	function updateWithChange(stateChanges: Partial<IFormState>) {
		const wasChanged = getWasChanged(stateChanges);

		updateStateForm(stateChanges);
		updateState({
			wasChanged,
		});
		onChange(wasChanged);
	}

	function setEmail() {
		updateWithChange({ email: userEmail });
	}

	async function dialogOnSave() {
		if (!checkEmail(stateForm.email)) {
			updateState({
				errorMsg: trans("userRole.error.email"),
			});

			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		let lastUpdate = "";
		let errorMsg = "";

		if (projectData.userRole === "owner" && stateForm.email === userEmail) {
			const createData = await apiCreateProjectWallet(projectData.id, projectData.lastUpdate, lang);

			if (createData.noErrors) {
				lastUpdate = createData.lastUpdate;
			} else {
				errorMsg = createData.errors.join(", ");
			}
		} else {
			const inviteData = await apiCreateProjectWalletInvitation(projectData.id, {
				requestedWalletEmail: stateForm.email,
				emailLanguage: lang,
				lastUpdate: projectData.lastUpdate,
			}, lang);

			if (inviteData.noErrors) {
				lastUpdate = inviteData.lastUpdate;
			} else {
				errorMsg = inviteData.errors.join(", ");
			}
		}

		updateState({
			dialogLoading: false,
			errorMsg,
		});

		if (lastUpdate) {
			onSave(lastUpdate);
		}
	}

	return <MyDialog title={switchWallet ? trans("walletConnectModal.titleConnect") : trans("walletConnectModal.titleSwitch")} onClose={onClose} onSave={dialogOnSave} dialogLoading={state.dialogLoading} errorMsg={state.errorMsg}
		cancelCaption={ trans("cancel") } saveCaption={ trans("walletConnectModal.save")} disableSave={!state.wasChanged} confirmClose={state.wasChanged} className="walletConnectModal">
		<p>
			{ trans("walletConnectModal.desc1") }
		</p>
		<p className="walletConnectModal__desc2">
			{ trans("walletConnectModal.desc2") }
		</p>
		<TextField
			label={trans("adminInternalUsersPage.filter.types.email")}
			value={stateForm.email}
			style={{ width: "100%" }}
			onChange={event => updateWithChange({ email: event.target.value })}
			color="success"
			autoComplete="off"
			sx={{ marginBottom: "var(--margin-large)" }}
			helperText={<ButtonLink title={trans("walletConnectModal.useEmail", { email: userEmail })} onClick={setEmail} />}
			size="small"
			FormHelperTextProps={{
				sx: { textAlign: "right", padding: "10px 0 0 0" },
			}}
		/>
		<p className="margin-top-small">
			{ trans("walletConnectModal.createInfo1") }&nbsp;
			<ExternalLink to={WALLET_REGISTER[lang]} sx={{ fontSize: "var(--default-font-size)" }}>
				{ trans("walletConnectModal.register") }
			</ExternalLink>.<br />
			{ trans("walletConnectModal.createInfo2") }&nbsp;
			<ExternalLink to={WALLET_DOCUMENTATION[lang]} sx={{ fontSize: "var(--default-font-size)" }}>
				{ trans("walletConnectModal.documentation") }
			</ExternalLink>.
		</p>
	</MyDialog>;
}
